import { useMemo } from 'react';
import { isDisplayableLabel } from '@utils/labels';
import type { SearchResult } from '../types';

export interface SearchFacets {
  categories: string[];
  fileTypes: string[];
  tags: string[];
  uploadedBy: string[];
}

const EMPTY: SearchFacets = { categories: [], fileTypes: [], tags: [], uploadedBy: [] };

const extension = (mimeType?: string) => mimeType?.split('/').pop()?.toLowerCase();

const sortedUnique = (values: Array<string | undefined>) =>
  Array.from(new Set(values.filter(isDisplayableLabel))).sort();

/**
 * Filter options taken from the results in hand. The search endpoint returns no
 * facets, and the lists this replaced were invented — 'Finance', 'John Doe',
 * 'quarterly' — so every one of them matched nothing when selected. Deriving
 * them means the offered options are exactly the ones that can narrow the set.
 */
export function useSearchFacets(results: SearchResult[]): SearchFacets {
  return useMemo(() => {
    if (results.length === 0) return EMPTY;

    return {
      categories: sortedUnique(results.map((result) => result.category)),
      fileTypes: sortedUnique(results.map((result) => extension(result.mimeType))),
      tags: sortedUnique(results.flatMap((result) => result.tags ?? [])),
      uploadedBy: sortedUnique(results.map((result) => result.uploadedBy)),
    };
  }, [results]);
}
